/*@flow*/
import type { Node } from 'react';
import React, { useState, useEffect } from 'react';
import unique from 'lib/unique';
import * as log from 'lib/log';
import filterByLabel from 'lib/filterByLabel';
import Select from 'react-select';
import { Creatable } from 'react-select';
import Task from 'folktale/concurrency/task';

export const customStyles = {
  input: (base: Object): any => ({ ...base, border: 'none' }),
  menu: (base: Object): any => ({ ...base, maxWidth: 400, zIndex: 999 }),
  valueContainer: (base: Object): any => ({ ...base, paddingLeft: 0 }),
  control: (base: Object): any => ({
    ...base,
    border: 'none',
    borderBottom: 'solid 1px #ccc',
    borderRadius: 0,
    maxWidth: 400,
    boxShadow: 'none',
  }),
};

const isOption = (o) => typeof o === 'object' && o && o.value && o.label;
const buildOption = (o) => (isOption(o) ? o : { value: o, label: o });
const extractValue = (o) => (isOption(o) ? o && o.value : o);
const tx = (fn) => (v) => (Array.isArray(v) ? v.map((o) => fn(o)) : fn(v));
const value2Option = tx(buildOption);
const option2Value = tx(extractValue);
const nextValue = ({ value }) => (dv: any | any[]) => {
  if (!dv) {
    return dv;
  } else if (!Array.isArray(value)) {
    return dv;
  } else if (!Array.isArray(dv)) {
    return unique(value.concat([dv]));
  }
  const nv = unique(value.concat(dv));
  if (nv.length !== value.length) {
    return nv;
  }
  // Remove elements of dv from value.
  return value.filter((v) => dv.indexOf(v) !== -1);
};

type OptionT = { value: any, label: string };
type InputPropsT = {
  // Required
  onChange: (value: any[] | any) => void,
  isMulti: boolean,
  // Optional
  loadValues?: (input?: string) => Promise<any[]>,
  loadOptions?: (input?: string) => Promise<any[]>,
  value?: any | any[],
  name?: string,
  className?: string,
  classNamePrefix?: string,
  isCreatable?: boolean,
  placeholder?: ?string,
  styles?: Object,
  noOptionsMessage?: string,
  isLoading?: boolean,
  isDisabled?: boolean,
};

const Component = (props: InputPropsT): Node => {
  const Comp = props.isCreatable ? Creatable : Select;
  const compValue = props.value ? value2Option(props.value) : props.value;

  const [options, setOptions] = useState([]);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (!isLoading) return;

    const getOptions = () => {
      if (props.loadValues) {
        const valueTask = Task.fromPromised(props.loadValues);
        const v = valueTask().map(value2Option);
        return v;
      } else if (props.loadOptions) {
        const optionsTask = Task.fromPromised(props.loadOptions);
        return optionsTask();
      }
      throw new Error('Required arguments loadValues or loadOptions missing.');
    };

    const taskExecution = getOptions().run();

    taskExecution.listen({
      onResolved: (o) => {
        setOptions(o);
        setLoading(false);
      },
      onCancelled() {},
      onRejected(e) {
        log.error(e);
        setLoading(false);
      },
    });

    return () => {
      taskExecution.cancel();
      setLoading(false);
    };
  }, [isLoading]);

  const onChange = (o: OptionT) => {
    const clickedValue = option2Value(o);
    const newValue = nextValue(props)(clickedValue);
    props.onChange(newValue);
  };

  const showLoading = Boolean(isLoading || props.isLoading);

  return (
    <Comp
      isDisabled={props.isDisabled}
      isMulti={props.isMulti}
      styles={{ ...customStyles, ...(props.styles || {}) }}
      placeholder={props.placeholder}
      options={options}
      filterOption={filterByLabel}
      value={compValue}
      cacheOptions
      defaultOptions
      isClearable
      noOptionsMessage={() => props.noOptionsMessage || 'No options'}
      autoFocus={false}
      name={props.name}
      className={props.className}
      classNamePrefix={props.classNamePrefix || 'Select'}
      onChange={onChange}
      isLoading={showLoading}
      onMenuOpen={() => setLoading(true)}
      onFocus={() => {
        setLoading(true);
      }}
    />
  );
};

export default Component;
